/**
 * PulseFit Workout Comparison View Controller
 * 
 * Manages head-to-head telemetry comparisons, stage-by-stage split differences,
 * dynamic performance winner determination, and on-demand AI Coach comparative analysis.
 */

import { store, saveStateToStorage } from '../../store.js';
import { escapeHtml, isDistanceStation, clearWorkoutSelection } from './workouts.js';
import { getStationBadgeHtml } from '../telemetry/index.js';
import {
  generateTelemetryPrompt,
  generateAIComparisonPrompt,
  callGeminiCoach
} from '../ai-coaching/index.js';
import { showView } from './router.js';

let activeComparisonW1 = null;
let activeComparisonW2 = null;
let isAnalyzingComparison = false;
let isComparisonInitialized = false;

export function getActiveComparisonWorkouts() {
  return { w1: activeComparisonW1, w2: activeComparisonW2 };
}

export function setActiveComparisonWorkouts(w1, w2) {
  activeComparisonW1 = w1;
  activeComparisonW2 = w2;
}

/**
 * Returns color-coded hex for subjective recovery metrics.
 */
export function getSubjectiveMetricColor(type, value) {
  if (value === undefined || value === null) return '';
  const val = parseFloat(value);
  if (isNaN(val)) return '';

  switch (type) {
    case 'feeling':
      if (val < 5) return '#ef4444';
      if (val <= 7) return '#fbbf24';
      return '#10b981';
    case 'restingHr':
      if (val < 62) return '#10b981';
      if (val <= 68) return '#fbbf24';
      return '#ef4444';
    case 'hrv':
      if (val > 34) return '#10b981';
      if (val >= 30) return '#fbbf24';
      return '#ef4444';
    case 'sleep':
      if (val < 6.0) return '#ef4444';
      if (val <= 6.5) return '#fbbf24';
      return '#10b981';
    case 'sleepScore':
      if (val > 80) return '#10b981';
      if (val >= 65) return '#fbbf24';
      return '#ef4444';
    default:
      return '';
  }
}

/**
 * Evaluates and displays the overall workout performance winner.
 */
export function updateOverallWinner(w1, w2) {
  if (typeof document === 'undefined') return;
  const criteriaSelect = document.getElementById('compare-winner-criteria');
  const winnerText = document.getElementById('compare-winner-text');
  const banner = document.getElementById('compare-winner-banner');
  if (!winnerText || !banner || !w1 || !w2) return;

  const criteria = criteriaSelect ? criteriaSelect.value : 'auto';
  let methodUsed = '';
  let winner = null; // 1 for w1, 2 for w2, 0 for tie, null for missing

  const w1Stages = w1.stages || [];
  const w2Stages = w2.stages || [];

  let finalMethod = criteria;
  if (criteria === 'auto') {
    if (w1Stages.length > 0 && w2Stages.length > 0 && w1Stages.length === w2Stages.length) {
      finalMethod = 'time';
      methodUsed = 'Auto: Same stage count (Shortest Time)';
    } else {
      finalMethod = 'hr';
      methodUsed = 'Auto: Different stage count (Lowest Avg HR)';
    }
  } else if (criteria === 'time') {
    methodUsed = 'Manual Mode: Shortest Time';
  } else {
    methodUsed = 'Manual Mode: Lowest Avg HR';
  }

  if (finalMethod === 'time') {
    const t1 = w1.duration || 0;
    const t2 = w2.duration || 0;
    if (t1 === 0 && t2 === 0) {
      winner = 0;
    } else if (t1 === t2) {
      winner = 0;
    } else {
      winner = t1 < t2 ? 1 : 2;
    }
  } else {
    const hr1 = w1.avgHeartrate || w1.avgHeartRate || 0;
    const hr2 = w2.avgHeartrate || w2.avgHeartRate || 0;
    if (hr1 === 0 && hr2 === 0) {
      winner = 0;
    } else if (hr1 === hr2) {
      winner = 0;
    } else if (hr1 === 0) {
      winner = 2;
    } else if (hr2 === 0) {
      winner = 1;
    } else {
      winner = hr1 < hr2 ? 1 : 2;
    }
  }

  const w1Title = `${escapeHtml(w1.type || 'Workout')} (${w1.date})`;
  const w2Title = `${escapeHtml(w2.type || 'Workout')} (${w2.date})`;

  if (winner === 1) {
    winnerText.innerHTML = `<span style="color:#06b6d4;">${w1Title}</span> is the best! 🏆 <span style="font-size:11px; font-weight: 500; color:var(--text-muted); margin-left: 6px;">(${methodUsed})</span>`;
    banner.style.borderLeftColor = '#06b6d4';
    banner.style.boxShadow = '0 4px 20px rgba(6, 182, 212, 0.15)';
  } else if (winner === 2) {
    winnerText.innerHTML = `<span style="color:#818cf8;">${w2Title}</span> is the best! 🏆 <span style="font-size:11px; font-weight: 500; color:var(--text-muted); margin-left: 6px;">(${methodUsed})</span>`;
    banner.style.borderLeftColor = '#818cf8';
    banner.style.boxShadow = '0 4px 20px rgba(129, 140, 248, 0.15)';
  } else if (winner === 0) {
    winnerText.innerHTML = `It's a Tie! 🤝 <span style="font-size:11px; font-weight: 500; color:var(--text-muted); margin-left: 6px;">(${methodUsed})</span>`;
    banner.style.borderLeftColor = 'var(--color-primary)';
    banner.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.15)';
  } else {
    winnerText.innerText = 'Unable to determine winner due to missing data.';
    banner.style.borderLeftColor = 'var(--border-light)';
    banner.style.boxShadow = 'none';
  }
}

/**
 * Renders Workout 1 and Workout 2 overview cards with subjective recovery metrics.
 */
export function renderComparisonWorkoutCards(w1, w2) {
  if (typeof document === 'undefined') return;
  const card1 = document.getElementById('compare-workout-1-card');
  const card2 = document.getElementById('compare-workout-2-card');
  if (!card1 || !card2) return;

  const formatCard = (workout, themeColor) => {
    let emoji = '💪';
    const typeLower = (workout.type || '').toLowerCase();
    if (typeLower.includes('run')) emoji = '🏃';
    else if (typeLower.includes('cycle')) emoji = '🚴';
    else if (typeLower.includes('strength')) emoji = '🏋️';
    else if (typeLower.includes('walk')) emoji = '🚶';
    else if (typeLower.includes('yoga')) emoji = '🧘';
    else if (typeLower.includes('swim')) emoji = '🏊';
    else if (typeLower.includes('dance')) emoji = '💃';
    else if (typeLower.includes('hyrox')) emoji = '⚡';

    let timeDispStr = workout.date || '';
    if (workout.time) timeDispStr += ` at ${workout.time}`;

    let recoveryHtml = '';
    const s = workout.subjective || workout.sourceData;
    if (s) {
      const physical = s.physicalFeeling !== undefined && s.physicalFeeling !== null ? `${s.physicalFeeling}/10` : '--';
      const mental = s.mentalFeeling !== undefined && s.mentalFeeling !== null ? `${s.mentalFeeling}/10` : '--';
      const restingHr = s.restingHr ? `${s.restingHr} bpm` : '--';
      const hrv = s.hrv ? `${s.hrv} ms` : '--';
      const sleepScore = s.sleepScore ? `${s.sleepScore}/100` : '--';
      let sleepStr = '--';
      if (s.sleepHours !== undefined && s.sleepHours !== null) {
        sleepStr = `${s.sleepHours}h ${s.sleepMinutes || 0}m`;
      }

      const physColor = getSubjectiveMetricColor('feeling', s.physicalFeeling);
      const mentColor = getSubjectiveMetricColor('feeling', s.mentalFeeling);
      const restingHrColor = getSubjectiveMetricColor('restingHr', s.restingHr);
      const hrvColor = getSubjectiveMetricColor('hrv', s.hrv);
      const sleepHoursVal = s.sleepHours !== undefined && s.sleepHours !== null ? s.sleepHours + (s.sleepMinutes || 0)/60 : null;
      const sleepColor = getSubjectiveMetricColor('sleep', sleepHoursVal);
      const sleepScoreColor = getSubjectiveMetricColor('sleepScore', s.sleepScore);

      recoveryHtml = `
        <div style="border-top: 1px solid rgba(255,255,255,0.05); padding-top: 12px; margin-top: 12px;">
          <span style="display: block; font-size: 10px; color: var(--text-muted); text-transform: uppercase; margin-bottom: 8px; font-weight:600; letter-spacing: 0.5px;">🧠 Subjective Recovery</span>
          <div style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; font-size: 11px;">
            <div><span style="color:var(--text-muted); display:block; font-size:9px; text-transform:uppercase; margin-bottom:2px;">Feel (P/M)</span> <span style="font-weight:600; color:${physColor || 'var(--text-primary)'}; font-family: monospace;">${s.physicalFeeling !== undefined && s.physicalFeeling !== null ? s.physicalFeeling : '--'}</span>/<span style="font-weight:600; color:${mentColor || 'var(--text-primary)'}; font-family: monospace;">${s.mentalFeeling !== undefined && s.mentalFeeling !== null ? s.mentalFeeling : '--'}</span></div>
            <div><span style="color:var(--text-muted); display:block; font-size:9px; text-transform:uppercase; margin-bottom:2px;">Rest HR</span> <span style="font-weight:600; color:${restingHrColor || 'var(--text-primary)'}; font-family: monospace;">${restingHr}</span></div>
            <div><span style="color:var(--text-muted); display:block; font-size:9px; text-transform:uppercase; margin-bottom:2px;">HRV</span> <span style="font-weight:600; color:${hrvColor || 'var(--text-primary)'}; font-family: monospace;">${hrv}</span></div>
            <div><span style="color:var(--text-muted); display:block; font-size:9px; text-transform:uppercase; margin-bottom:2px;">Sleep</span> <span style="font-weight:600; color:${sleepColor || 'var(--text-primary)'}; font-family: monospace;">${sleepStr}</span></div>
            <div><span style="color:var(--text-muted); display:block; font-size:9px; text-transform:uppercase; margin-bottom:2px;">Score</span> <span style="font-weight:600; color:${sleepScoreColor || 'var(--text-primary)'}; font-family: monospace;">${sleepScore}</span></div>
          </div>
        </div>
      `;
    }

    const hrVal = workout.avgHeartrate || workout.avgHeartRate;

    return `
      <div style="display: flex; align-items: center; gap: 12px;">
        <span style="font-size: 32px;">${emoji}</span>
        <div>
          <h4 style="font-family: 'Outfit', sans-serif; font-size: 16px; font-weight: 700; margin: 0; color: ${themeColor};">${escapeHtml(workout.name || workout.type || 'Workout')}</h4>
          <span style="font-size: 12px; color: var(--text-muted);">${timeDispStr}</span>
        </div>
      </div>
      <div style="display: flex; gap: 24px; border-top: 1px solid rgba(255,255,255,0.05); padding-top: 12px; margin-top: 4px;">
        <div>
          <span style="display: block; font-size: 10px; color: var(--text-muted); text-transform: uppercase;">Duration</span>
          <span style="font-size: 18px; font-weight: 700; color: var(--text-primary); font-family: monospace;">${workout.duration || 0}m</span>
        </div>
        <div>
          <span style="display: block; font-size: 10px; color: var(--text-muted); text-transform: uppercase;">Energy</span>
          <span style="font-size: 18px; font-weight: 700; color: var(--text-primary); font-family: monospace;">${workout.calories || 0} kcal</span>
        </div>
        ${hrVal ? `
        <div>
          <span style="display: block; font-size: 10px; color: var(--text-muted); text-transform: uppercase;">Avg HR</span>
          <span style="font-size: 18px; font-weight: 700; color: #f43f5e; font-family: monospace;">❤️ ${hrVal} bpm</span>
        </div>
        ` : ''}
      </div>
      ${recoveryHtml}
    `;
  };

  card1.innerHTML = formatCard(w1, '#06b6d4');
  card2.innerHTML = formatCard(w2, '#818cf8');
}

/**
 * Renders the head-to-head metrics comparison table.
 */
export function renderHeadToHeadTable(w1, w2) {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('compare-metrics-table-container');
  if (!container) return;

  const formatVal = (val, suffix = '', def = 'N/A') => {
    return val !== undefined && val !== null ? `${val}${suffix}` : def;
  };

  const getBetterIndicator = (w1Val, w2Val, lowerIsBetter = false) => {
    if (w1Val === undefined || w1Val === null || w2Val === undefined || w2Val === null) return '';
    if (w1Val === w2Val) return '<span style="color:var(--text-muted);">Equal</span>';
    const w1IsBetter = lowerIsBetter ? (w1Val < w2Val) : (w1Val > w2Val);
    const diff = Math.abs(w1Val - w2Val);
    const diffText = diff % 1 === 0 ? diff : diff.toFixed(1);
    if (w1IsBetter) {
      return `<span style="color:#10b981; font-weight:600;">← W1 better by ${diffText}</span>`;
    } else {
      return `<span style="color:#a78bfa; font-weight:600;">W2 better by ${diffText} →</span>`;
    }
  };

  const w1Sub = w1.subjective || w1.sourceData || {};
  const w2Sub = w2.subjective || w2.sourceData || {};

  const w1AvgHr = w1.avgHeartrate || w1.avgHeartRate;
  const w2AvgHr = w2.avgHeartrate || w2.avgHeartRate;
  const w1MaxHr = w1.maxHeartrate || w1.maxHeartRate;
  const w2MaxHr = w2.maxHeartrate || w2.maxHeartRate;

  const rows = [
    { name: 'Duration', w1: w1.duration, w2: w2.duration, suffix: ' min', lowerIsBetter: true, comp: getBetterIndicator(w1.duration, w2.duration, true) },
    { name: 'Calories Burned', w1: w1.calories, w2: w2.calories, suffix: ' kcal', lowerIsBetter: false, comp: getBetterIndicator(w1.calories, w2.calories, false) },
    { name: 'Avg Heart Rate', w1: w1AvgHr, w2: w2AvgHr, suffix: ' bpm', lowerIsBetter: true, comp: getBetterIndicator(w1AvgHr, w2AvgHr, true) },
    { name: 'Max Heart Rate', w1: w1MaxHr, w2: w2MaxHr, suffix: ' bpm', lowerIsBetter: true, comp: getBetterIndicator(w1MaxHr, w2MaxHr, true) },
    { name: 'Suffer Score', w1: w1.sufferScore, w2: w2.sufferScore, lowerIsBetter: false, comp: getBetterIndicator(w1.sufferScore, w2.sufferScore, false) },
    { name: 'Physical Feeling', w1: w1Sub.physicalFeeling, w2: w2Sub.physicalFeeling, suffix: ' / 10', lowerIsBetter: false, comp: getBetterIndicator(w1Sub.physicalFeeling, w2Sub.physicalFeeling, false) },
    { name: 'Mental Feeling', w1: w1Sub.mentalFeeling, w2: w2Sub.mentalFeeling, suffix: ' / 10', lowerIsBetter: false, comp: getBetterIndicator(w1Sub.mentalFeeling, w2Sub.mentalFeeling, false) },
    { name: 'Resting HR', w1: w1Sub.restingHr, w2: w2Sub.restingHr, suffix: ' bpm', lowerIsBetter: true, comp: getBetterIndicator(w1Sub.restingHr, w2Sub.restingHr, true) },
    { name: 'HRV', w1: w1Sub.hrv, w2: w2Sub.hrv, suffix: ' ms', lowerIsBetter: false, comp: getBetterIndicator(w1Sub.hrv, w2Sub.hrv, false) },
    { 
      name: 'Amount of Sleep', 
      w1: w1Sub.sleepHours !== undefined && w1Sub.sleepHours !== null ? (w1Sub.sleepHours + (w1Sub.sleepMinutes || 0)/60) : null,
      w2: w2Sub.sleepHours !== undefined && w2Sub.sleepHours !== null ? (w2Sub.sleepHours + (w2Sub.sleepMinutes || 0)/60) : null,
      customVal: true,
      lowerIsBetter: false,
      w1Disp: w1Sub.sleepHours !== undefined && w1Sub.sleepHours !== null ? `${w1Sub.sleepHours}h ${w1Sub.sleepMinutes || 0}m` : 'N/A',
      w2Disp: w2Sub.sleepHours !== undefined && w2Sub.sleepHours !== null ? `${w2Sub.sleepHours}h ${w2Sub.sleepMinutes || 0}m` : 'N/A',
      comp: getBetterIndicator(
        w1Sub.sleepHours !== undefined && w1Sub.sleepHours !== null ? (w1Sub.sleepHours + (w1Sub.sleepMinutes || 0)/60) : null,
        w2Sub.sleepHours !== undefined && w2Sub.sleepHours !== null ? (w2Sub.sleepHours + (w2Sub.sleepMinutes || 0)/60) : null,
        false
      )
    },
    { name: 'Sleep Score', w1: w1Sub.sleepScore, w2: w2Sub.sleepScore, suffix: ' / 100', lowerIsBetter: false, comp: getBetterIndicator(w1Sub.sleepScore, w2Sub.sleepScore, false) }
  ];

  let rowsHtml = '';
  rows.forEach(r => {
    const hasAnyVals = (r.w1 !== undefined && r.w1 !== null) || (r.w2 !== undefined && r.w2 !== null);
    if (!hasAnyVals) return;

    let w1Check = '';
    let w2Check = '';
    
    if (r.w1 !== undefined && r.w1 !== null && r.w2 !== undefined && r.w2 !== null && r.w1 !== r.w2) {
      const w1IsBetter = r.lowerIsBetter ? (r.w1 < r.w2) : (r.w1 > r.w2);
      if (w1IsBetter) {
        w1Check = ' <span style="color:#10b981; font-weight:bold; margin-left: 6px;">✓</span>';
      } else {
        w2Check = ' <span style="color:#10b981; font-weight:bold; margin-left: 6px;">✓</span>';
      }
    }

    const w1ValDisp = r.customVal ? r.w1Disp : formatVal(r.w1, r.suffix);
    const w2ValDisp = r.customVal ? r.w2Disp : formatVal(r.w2, r.suffix);

    rowsHtml += `
      <tr style="border-bottom: 1px solid rgba(255,255,255,0.05); transition: background 0.2s;">
        <td style="padding: 12px; font-weight: 500; color: var(--text-secondary);">${r.name}</td>
        <td style="padding: 12px; font-family: monospace; ${w1Check ? 'color:#10b981; font-weight:600;' : ''}">${w1ValDisp}${w1Check}</td>
        <td style="padding: 12px; font-family: monospace; ${w2Check ? 'color:#10b981; font-weight:600;' : ''}">${w2ValDisp}${w2Check}</td>
        <td style="padding: 12px; text-align: center;">${r.comp}</td>
      </tr>
    `;
  });

  const w1Header = `${escapeHtml(w1.type || 'Workout')} (${w1.date})`;
  const w2Header = `${escapeHtml(w2.type || 'Workout')} (${w2.date})`;

  container.innerHTML = `
    <table class="compare-table" style="width: 100%; border-collapse: collapse; text-align: left; font-size: 13px;">
      <thead>
        <tr style="border-bottom: 1px solid var(--border-light); font-weight: 600; color: var(--text-muted);">
          <th style="padding: 10px 12px;">Metric</th>
          <th style="padding: 10px 12px; color: #06b6d4;">${w1Header}</th>
          <th style="padding: 10px 12px; color: #818cf8;">${w2Header}</th>
          <th style="padding: 10px 12px; text-align: center;">Comparison Difference</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  `;
}

/**
 * Renders stage-by-stage splits table contrasting pacing seconds and heart rate.
 */
export function renderSplitsComparisonTable(w1, w2) {
  if (typeof document === 'undefined') return;
  const container = document.getElementById('compare-splits-table-container');
  const cardSection = document.getElementById('compare-splits-card');
  if (!container || !cardSection) return;

  const w1Stages = w1.stages || [];
  const w2Stages = w2.stages || [];

  if (w1Stages.length === 0 && w2Stages.length === 0) {
    cardSection.style.display = 'none';
    return;
  }
  
  cardSection.style.display = 'block';
  const totalStagesCount = Math.max(w1Stages.length, w2Stages.length);

  const getSecs = (stage) => {
    if (!stage) return null;
    return (stage.durationMin * 60) + (stage.durationSec || 0);
  };

  const formatSec = (totalSec) => {
    if (totalSec === null || totalSec === undefined) return '--';
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  let rowsHtml = '';
  for (let i = 0; i < totalStagesCount; i++) {
    const s1 = w1Stages[i] || null;
    const s2 = w2Stages[i] || null;

    const s1Sec = getSecs(s1);
    const s2Sec = getSecs(s2);

    const s1Hr = s1 ? s1.avgHr : null;
    const s2Hr = s2 ? s2.avgHr : null;

    const stageNum = i + 1;
    const activeStage = s1 || s2;
    const isRun = activeStage.type === 'Run';
    const isRox = activeStage.type === 'RoxZone' || (activeStage.stationName && activeStage.stationName.toLowerCase().includes('rox'));
    const stationKey = isRun ? 'Run' : (isRox ? 'RoxZone' : (activeStage.stationName || 'Run'));
    const badge = getStationBadgeHtml(stationKey, 'station-badge-xs');
    const stageName = `<span style="display:inline-flex; align-items:center; gap:6px;">${badge} <span>${isRun ? 'Run' : escapeHtml(activeStage.stationName || 'Station')}</span></span>`;

    let timeDiffHtml = '--';
    let s1TimeStyle = '';
    let s2TimeStyle = '';
    let w1TimeCheck = '';
    let w2TimeCheck = '';
    
    if (s1Sec !== null && s2Sec !== null) {
      const diff = s2Sec - s1Sec;
      if (diff === 0) {
        timeDiffHtml = `<span style="color:var(--text-muted);">Equal</span>`;
      } else if (diff < 0) {
        timeDiffHtml = `<span style="color:#10b981; font-weight:600;">-${Math.abs(diff)}s (W2 faster)</span>`;
        s2TimeStyle = 'color:#10b981; font-weight:600;';
        s1TimeStyle = 'color:var(--text-muted);';
        w2TimeCheck = ' <span style="color:#10b981; font-weight:bold;">✓</span>';
      } else {
        timeDiffHtml = `<span style="color:#a78bfa; font-weight:600;">+${diff}s (W1 faster)</span>`;
        s1TimeStyle = 'color:#10b981; font-weight:600;';
        s2TimeStyle = 'color:var(--text-muted);';
        w1TimeCheck = ' <span style="color:#10b981; font-weight:bold;">✓</span>';
      }
    }

    let hrDiffHtml = '--';
    let s1HrStyle = '';
    let s2HrStyle = '';
    let w1HrCheck = '';
    let w2HrCheck = '';
    
    if (s1Hr && s2Hr) {
      const diff = s2Hr - s1Hr;
      if (diff === 0) {
        hrDiffHtml = `<span style="color:var(--text-muted);">Equal</span>`;
      } else if (diff < 0) {
        hrDiffHtml = `<span style="color:#10b981; font-weight:600;">-${Math.abs(diff)} bpm (W2 lower)</span>`;
        s2HrStyle = 'color:#10b981; font-weight:600;';
        s1HrStyle = 'color:var(--text-muted);';
        w2HrCheck = ' <span style="color:#10b981; font-weight:bold;">✓</span>';
      } else {
        hrDiffHtml = `<span style="color:#a78bfa; font-weight:600;">+${diff} bpm (W1 lower)</span>`;
        s1HrStyle = 'color:#10b981; font-weight:600;';
        s2HrStyle = 'color:var(--text-muted);';
        w1HrCheck = ' <span style="color:#10b981; font-weight:bold;">✓</span>';
      }
    }

    rowsHtml += `
      <tr style="border-bottom: 1px solid rgba(255,255,255,0.05); transition: background 0.2s;">
        <td style="padding: 12px; font-weight: 600; color: var(--text-muted); text-align: center;">${stageNum}</td>
        <td style="padding: 12px; font-weight: 500; color: var(--text-secondary);">${stageName}</td>
        <td style="padding: 12px; text-align: center; font-family: monospace; ${s1TimeStyle}">${formatSec(s1Sec)}${w1TimeCheck}</td>
        <td style="padding: 12px; text-align: center; font-family: monospace; ${s2TimeStyle}">${formatSec(s2Sec)}${w2TimeCheck}</td>
        <td style="padding: 12px; text-align: center; font-family: monospace;">${timeDiffHtml}</td>
        <td style="padding: 12px; text-align: center; font-family: monospace; ${s1HrStyle}">${s1Hr ? s1Hr + ' bpm' : '--'}${w1HrCheck}</td>
        <td style="padding: 12px; text-align: center; font-family: monospace; ${s2HrStyle}">${s2Hr ? s2Hr + ' bpm' : '--'}${w2HrCheck}</td>
        <td style="padding: 12px; text-align: center; font-family: monospace;">${hrDiffHtml}</td>
      </tr>
    `;
  }

  container.innerHTML = `
    <table class="compare-table" style="width: 100%; border-collapse: collapse; text-align: left; font-size: 13px;">
      <thead>
        <tr style="border-bottom: 1px solid var(--border-light); font-weight: 600; color: var(--text-muted);">
          <th style="padding: 10px 12px; text-align: center; width: 60px;">Stage</th>
          <th style="padding: 10px 12px;">Stage Type / Name</th>
          <th style="padding: 10px 12px; text-align: center; color: #06b6d4;">W1 Time</th>
          <th style="padding: 10px 12px; text-align: center; color: #818cf8;">W2 Time</th>
          <th style="padding: 10px 12px; text-align: center;">Time Diff</th>
          <th style="padding: 10px 12px; text-align: center; color: #06b6d4;">W1 Avg HR</th>
          <th style="padding: 10px 12px; text-align: center; color: #818cf8;">W2 Avg HR</th>
          <th style="padding: 10px 12px; text-align: center;">HR Diff</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>
  `;
}

/**
 * Executes AI Coach comparative analysis via FastAPI backend.
 */
export async function executeAIComparativeAnalysis(w1, w2) {
  if (isAnalyzingComparison) return;
  isAnalyzingComparison = true;

  const statusBadge = document.getElementById('compare-ai-status-badge');
  const aiContainer = document.getElementById('compare-ai-analysis-container');
  if (statusBadge) statusBadge.innerText = '⚡ Analyzing...';

  if (aiContainer) {
    aiContainer.innerHTML = `
      <div style="text-align: center; padding: 36px 20px; font-size: 13px; color: var(--text-muted);">
        <div style="font-size: 24px; margin-bottom: 10px; animation: pulse 1.5s infinite;">🤖</div>
        <div style="color: var(--text-primary); font-weight: 600; margin-bottom: 4px;">Activating AI Athletic Coach...</div>
        <div>Contrasting physiological telemetry, pacing efficiency, and recovery markers...</div>
      </div>
    `;
  }

  const cacheKey = [w1.id, w2.id].sort().join('_');

  try {
    const tel1 = generateTelemetryPrompt(w1, isDistanceStation);
    const tel2 = generateTelemetryPrompt(w2, isDistanceStation);
    const promptText = generateAIComparisonPrompt(tel1, tel2, store.geminiInstructions);

    const supabase = (typeof window !== 'undefined') ? window.supabaseClient : null;
    const outputText = await callGeminiCoach(promptText, {
      supabaseClient: supabase,
      customKey: store.geminiApiKey,
      systemInstruction: store.geminiInstructions
    });

    let parsedHtml = outputText;
    parsedHtml = parsedHtml.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    parsedHtml = parsedHtml.replace(/\*(.*?)\*/g, '<em>$1</em>');
    parsedHtml = parsedHtml.replace(/^\s*(\d+)\.\s+(.*?)$/gm, '<h4 style="font-family: \'Outfit\', sans-serif; font-size: 14px; font-weight: 700; margin-top: 20px; margin-bottom: 12px; color: var(--color-primary); display: flex; align-items: center; gap: 6px;"><span>$1.</span> <span>$2</span></h4>');
    parsedHtml = parsedHtml.replace(/^\s*[-*]\s+(.*?)$/gm, '<div style="display: flex; gap: 8px; align-items: flex-start; margin-bottom: 8px; font-size: 13px; line-height: 1.6; color: var(--text-muted);"><span style="color: var(--color-primary); font-weight: bold; font-size: 11px; margin-top: 2px;">✦</span><div>$1</div></div>');
    parsedHtml = parsedHtml.replace(/\n\n/g, '<div style="margin-bottom: 12px;"></div>');

    parsedHtml += `
      <div style="margin-top: 20px; padding-top: 14px; border-top: 1px solid var(--border-light); display: flex; justify-content: flex-end;">
        <button id="btn-refresh-ai-comparison" class="btn btn-secondary" style="font-size: 11px; padding: 6px 12px;">
          🔄 Re-analyze Comparison
        </button>
      </div>
    `;

    if (!store.comparisons) store.comparisons = {};
    store.comparisons[cacheKey] = parsedHtml;
    saveStateToStorage();

    if (aiContainer) aiContainer.innerHTML = parsedHtml;
    if (statusBadge) statusBadge.innerText = '🤖 Analysis Ready';

    const refreshBtn = document.getElementById('btn-refresh-ai-comparison');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        executeAIComparativeAnalysis(w1, w2);
      });
    }
  } catch (err) {
    console.error('AI comparison failure:', err);
    if (aiContainer) {
      aiContainer.innerHTML = `
        <div style="color: #f87171; font-weight: 600; margin-bottom: 8px;">⚠️ Comparison Error</div>
        <div style="font-size: 12px; color: var(--text-muted); line-height: 1.5;">
          ${escapeHtml(err.message)}<br><br>
          <em>Note: Ensure you have added a working Gemini API Key in the sidebar "AI Coach Setup" form and have active network access.</em>
        </div>
        <div style="margin-top: 14px;">
          <button id="btn-retry-ai-comparison" class="btn btn-secondary" style="font-size: 11px; padding: 6px 12px;">
            🔄 Retry Analysis
          </button>
        </div>
      `;
      const retryBtn = document.getElementById('btn-retry-ai-comparison');
      if (retryBtn) {
        retryBtn.addEventListener('click', () => {
          executeAIComparativeAnalysis(w1, w2);
        });
      }
    }
    if (statusBadge) statusBadge.innerText = '⚠️ Error';
  } finally {
    isAnalyzingComparison = false;
  }
}

/**
 * Opens the comparison view and renders all visual telemetry widgets.
 * Prepares the AI Coach comparative analysis in on-demand state.
 */
export function openWorkoutComparison(w1, w2) {
  if (!w1 || !w2) return;
  activeComparisonW1 = w1;
  activeComparisonW2 = w2;

  showView('view-workout-comparison');

  const statusBadge = document.getElementById('compare-ai-status-badge');
  const aiContainer = document.getElementById('compare-ai-analysis-container');
  if (statusBadge) statusBadge.innerText = '⚡ Visual Telemetry Ready';

  // Render Visual Widgets
  renderComparisonWorkoutCards(w1, w2);
  renderHeadToHeadTable(w1, w2);
  renderSplitsComparisonTable(w1, w2);

  // Criteria Selection & Winner Setup
  const criteriaSelect = document.getElementById('compare-winner-criteria');
  if (criteriaSelect) {
    criteriaSelect.value = 'auto';
  }
  updateOverallWinner(w1, w2);

  // Check cached AI analysis or render on-demand action trigger
  const cacheKey = [w1.id, w2.id].sort().join('_');
  if (store.comparisons && store.comparisons[cacheKey]) {
    if (aiContainer) aiContainer.innerHTML = store.comparisons[cacheKey];
    if (statusBadge) statusBadge.innerText = '🤖 Analysis Ready (Cached)';

    const refreshBtn = document.getElementById('btn-refresh-ai-comparison');
    if (refreshBtn) {
      refreshBtn.addEventListener('click', () => {
        executeAIComparativeAnalysis(w1, w2);
      });
    }
  } else {
    if (aiContainer) {
      aiContainer.innerHTML = `
        <div style="text-align: center; padding: 28px 16px;">
          <p style="color: var(--text-secondary); font-size: 13px; margin-bottom: 16px;">
            Get an in-depth, professional comparative analysis contrasting pacing, heart rate load, and physiological efficiency targets.
          </p>
          <button id="btn-trigger-ai-comparison" class="btn btn-primary" style="font-size: 12px; padding: 8px 18px; border-radius: var(--radius-xs); background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%); border: none; box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);">
            🤖 Generate AI Comparative Analysis
          </button>
        </div>
      `;
      const triggerBtn = document.getElementById('btn-trigger-ai-comparison');
      if (triggerBtn) {
        triggerBtn.addEventListener('click', () => {
          executeAIComparativeAnalysis(w1, w2);
        });
      }
    }
  }
}

export const generateAIComparison = openWorkoutComparison;

/**
 * Initializes the comparison view controls and listeners.
 */
export function initComparisonView() {
  if (typeof document === 'undefined') return;
  if (isComparisonInitialized) return;
  isComparisonInitialized = true;

  const backBtn = document.getElementById('btn-back-to-workouts-compare');
  if (backBtn) {
    backBtn.addEventListener('click', () => {
      clearWorkoutSelection();
      showView('view-workouts');
    });
  }

  const criteriaSelect = document.getElementById('compare-winner-criteria');
  if (criteriaSelect) {
    criteriaSelect.addEventListener('change', () => {
      if (activeComparisonW1 && activeComparisonW2) {
        updateOverallWinner(activeComparisonW1, activeComparisonW2);
      }
    });
  }
}
